import type {
  FirebaseFirestoreTypes,
  Timestamp,
} from "@react-native-firebase/firestore";
import { useTranslation } from "react-i18next";

type SecondsAndNanoseconds = { seconds: number; nanoseconds: number };
type TimestampInput =
  | Timestamp
  | FirebaseFirestoreTypes.Timestamp
  | SecondsAndNanoseconds
  | null
  | undefined;

export function useFormatDate() {
  const { i18n } = useTranslation();

  const formatDate = (date: TimestampInput): string => {
    if (!date) return "";

    const dateObj = new Date(date.seconds * 1000 + date.nanoseconds / 1000000);
    const day = String(dateObj.getDate()).padStart(2, "0");
    const month = dateObj
      .toLocaleString(i18n.language, { month: "short" })
      .toUpperCase();
    const year = dateObj.getFullYear();

    return `${day}/${month}/${year}`;
  };

  const formatMonthYear = (date: TimestampInput): string => {
    if (!date) return "";

    const dateObj = new Date(date.seconds * 1000 + date.nanoseconds / 1000000);
    const month = dateObj.toLocaleString(i18n.language, { month: "long" });
    const capitalizedMonth = month.charAt(0).toUpperCase() + month.slice(1);
    const year = dateObj.getFullYear();

    return `${capitalizedMonth} ${year}`;
  };

  return { formatDate, formatMonthYear };
}

export function useFormatDateTime() {
  const { i18n } = useTranslation();

  const formatDateTime = (date: TimestampInput): string => {
    if (!date) return "";

    const dateObj = new Date(date.seconds * 1000 + date.nanoseconds / 1000000);
    const day = String(dateObj.getDate()).padStart(2, "0");
    const month = dateObj.toLocaleString(i18n.language, { month: "short" });
    const year = dateObj.getFullYear();
    const hours = String(dateObj.getHours()).padStart(2, "0");
    const minutes = String(dateObj.getMinutes()).padStart(2, "0");

    return `${day} ${month}. ${year} · ${hours}:${minutes} hs`;
  };
  return { formatDateTime };
}

export const useFormatTimeAgo = () => {
  const { t } = useTranslation();

  const formatTimeAgo = (dateString: string | null | undefined): string => {
    if (!dateString) return "";
    const date = new Date(dateString);
    const seconds = Math.floor((new Date().getTime() - date.getTime()) / 1000);

    const intervals = [
      { label: t("YEAR_ABBREVIATION"), seconds: 31536000 },
      { label: t("MONTH_ABBREVIATION"), seconds: 2592000 },
      { label: t("DAY_ABBREVIATION"), seconds: 86400 },
      { label: t("HOUR_ABBREVIATION"), seconds: 3600 },
      { label: t("MINUTE_ABBREVIATION"), seconds: 60 },
    ];

    for (const interval of intervals) {
      const count = Math.floor(seconds / interval.seconds);
      if (count >= 1) {
        return `${count}${interval.label}`;
      }
    }
    return t("JUST_NOW");
  };

  return { formatTimeAgo };
};
